import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
  SwipeableDrawer,
  AppBar,
  Toolbar,
  LinearProgress,
  Skeleton,
  Snackbar,
  Zoom,
  Grow,
  Slide,
  keyframes
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  Add,
  Refresh,
  ExpandMore,
  ExpandLess,
  Edit,
  Delete,
  AccessTime,
  CalendarMonth,
  TrendingUp,
  Info,
  FilterList,
  Timeline,
  Today,
  Undo,
  SwipeLeft,
  SwipeRight,
  ChevronLeft,
  ChevronRight,
  TouchApp
} from '@mui/icons-material';
import moment from 'moment';
import { refreshUserData } from '../../services/authService';
import { getSystemSetting } from '../../services/systemSettingsService';

// Keyframe animations
const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const rippleAnimation = keyframes`
  0% { transform: scale(0); opacity: 0.5; }
  100% { transform: scale(4); opacity: 0; }
`;

const slideInLeft = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideInRight = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

// Shopee Icon Component
const ShopeeIcon = ({ size = 20, color = 'white' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M21.09 7.89L12.5 2.23c-.31-.21-.69-.21-1 0L2.91 7.89c-.39.26-.39.84 0 1.1l8.59 5.66c.31.21.69.21 1 0l8.59-5.66c.39-.26.39-.84 0-1.1z" fill={color}/>
    <path d="M3.5 13.55v4.38c0 .41.25.78.63.93l7.5 2.86c.24.09.5.09.74 0l7.5-2.86c.38-.15.63-.52.63-.93v-4.38" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// TikTok Icon Component
const TikTokIcon = ({ size = 20, color = 'white' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" fill={color}/>
  </svg>
);

const DEFAULT_TIME_SLOTS = [
  '07:00-10:00',
  '10:00-13:00',
  '11:00-14:00',
  '14:00-17:00',
  '16:00-19:00',
  '19:00-22:00',
  '20:00-23:00'
];

const DAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

const MobileScheduleSelector = ({
  userId,
  userName,
  userRole,
  weekStartDate,
  existingSchedules = [],
  onSubmit,
  onRefresh,
  onDelete,
  onWeekChange // New: callback for week navigation (swipe)
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS);
  const [selectedSlots, setSelectedSlots] = useState({});
  const [platform, setPlatform] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [weekDates, setWeekDates] = useState([]);
  const [expandedDay, setExpandedDay] = useState(null);
  const [quickAddDialog, setQuickAddDialog] = useState(false);
  const [quickAddData, setQuickAddData] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewType, setViewType] = useState('timeline'); // 'list' or 'timeline' - default timeline for overview
  const [allowedTimeSlots, setAllowedTimeSlots] = useState([]); // User's allowed time slots from admin
  
  // New UX states
  const [swipeDirection, setSwipeDirection] = useState(null); // 'left' | 'right' | null
  const [undoSnackbar, setUndoSnackbar] = useState({ open: false, scheduleId: null, scheduleData: null });
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [skeletonLoading, setSkeletonLoading] = useState(true);
  const [clickedSlot, setClickedSlot] = useState(null); // For ripple animation
  const [showCalendar, setShowCalendar] = useState(false); // Calendar Mini View
  const [calendarMonth, setCalendarMonth] = useState(moment()); // Current month for calendar
  const containerRef = useRef(null);
  const pullStartY = useRef(0);
  
  // Get user available platforms
  const getAvailablePlatforms = () => {
    if (userRole === 'admin') return ['shopee', 'tiktok'];
    if (userRole === 'live_shopee') return ['shopee'];
    if (userRole === 'live_tiktok') return ['tiktok'];
    if (userRole === 'live_multi') return ['shopee', 'tiktok'];
    return [];
  };
  
  const availablePlatforms = getAvailablePlatforms();
  
  // Helper function để kiểm tra xem time slot có được phép không
  const isTimeSlotAllowed = (dayIndex, timeSlot, selectedPlatform) => {
    // Nếu không có giới hạn, cho phép tất cả
    if (!allowedTimeSlots || allowedTimeSlots.length === 0) {
      return true;
    }
    
    // Parse time slot (format: "07:00-10:00")
    const [startTime, endTime] = timeSlot.split('-');
    
    // dayIndex: 0 = Monday, ..., 6 = Sunday trong weekDates
    // Nhưng allowedTimeSlots.dayOfWeek: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // Convert dayIndex to dayOfWeek
    const dayOfWeek = dayIndex === 6 ? 0 : dayIndex + 1;
    
    // Kiểm tra xem có slot nào match không
    const matchingSlot = allowedTimeSlots.find(slot => {
      const platformMatch = slot.platform === 'both' || 
                           slot.platform === selectedPlatform || 
                           !selectedPlatform; // Nếu chưa chọn platform thì cho phép
      
      const dayMatch = slot.dayOfWeek === dayOfWeek;
      const timeMatch = slot.startTime === startTime && slot.endTime === endTime;
      
      return platformMatch && dayMatch && timeMatch;
    });
    
    return !!matchingSlot;
  };
  
  // Filter time slots theo allowed slots
  const getFilteredTimeSlots = (dayIndex, selectedPlatform) => {
    if (!allowedTimeSlots || allowedTimeSlots.length === 0) {
      return timeSlots;
    }
    
    return timeSlots.filter(slot => isTimeSlotAllowed(dayIndex, slot, selectedPlatform));
  };
  
  // Load user's allowed time slots from localStorage
  const loadAllowedTimeSlots = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user.allowedTimeSlots && Array.isArray(user.allowedTimeSlots)) {
          setAllowedTimeSlots(user.allowedTimeSlots);
        } else {
          setAllowedTimeSlots([]);
        }
      }
    } catch (error) {
      console.error('Error loading allowed time slots:', error);
    }
  };

  const swipeStartX = useRef(0);
  const swipeStartY = useRef(0);
  
  useEffect(() => {
    loadAllowedTimeSlots();
    loadCustomTimeSlots();
    // Simulate initial skeleton loading
    const timer = setTimeout(() => setSkeletonLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);
  
  // Custom swipe handlers (no external dependency)
  const handleSwipeStart = useCallback((e) => {
    const touch = e.touches ? e.touches[0] : e;
    swipeStartX.current = touch.clientX;
    swipeStartY.current = touch.clientY;
  }, []);
  
  const handleSwipeMove = useCallback((e) => {
    if (!swipeStartX.current) return;
    const touch = e.touches ? e.touches[0] : e;
    const deltaX = touch.clientX - swipeStartX.current;
    const deltaY = touch.clientY - swipeStartY.current;
    
    // Only trigger if horizontal swipe is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      setSwipeDirection(deltaX > 0 ? 'right' : 'left');
    }
  }, []);
  
  const handleSwipeEnd = useCallback(() => {
    if (swipeDirection === 'left' && onWeekChange) {
      onWeekChange('next');
    } else if (swipeDirection === 'right' && onWeekChange) {
      onWeekChange('prev');
    }
    setTimeout(() => setSwipeDirection(null), 300);
    swipeStartX.current = 0;
    swipeStartY.current = 0;
  }, [swipeDirection, onWeekChange]);
  
  // Pull to refresh handlers
  const handleTouchStart = useCallback((e) => {
    if (containerRef.current?.scrollTop === 0) {
      pullStartY.current = e.touches[0].clientY;
    }
  }, []);
  
  const handleTouchMove = useCallback((e) => {
    if (pullStartY.current > 0 && containerRef.current?.scrollTop === 0) {
      const currentY = e.touches[0].clientY;
      const distance = currentY - pullStartY.current;
      if (distance > 0 && distance < 150) {
        setPullDistance(distance);
        setIsPulling(distance > 60);
      }
    }
  }, []);
  
  const handleTouchEnd = useCallback(() => {
    if (isPulling && onRefresh) {
      setLoading(true);
      onRefresh();
      setTimeout(() => setLoading(false), 1000);
    }
    setPullDistance(0);
    setIsPulling(false);
    pullStartY.current = 0;
  }, [isPulling, onRefresh]);
  
  // Undo delete handler
  const handleUndoDelete = useCallback(() => {
    // This would restore the deleted schedule
    // For now, just close the snackbar
    setUndoSnackbar({ open: false, scheduleId: null, scheduleData: null });
  }, []);
  
  // Handle delete with undo
  const handleDeleteWithUndo = useCallback((scheduleId, scheduleData) => {
    setUndoSnackbar({ open: true, scheduleId, scheduleData });
    // Actually delete after 5 seconds if not undone
    const timer = setTimeout(() => {
      if (onDelete) onDelete(scheduleId);
      setUndoSnackbar({ open: false, scheduleId: null, scheduleData: null });
    }, 5000);
    // Store timer to cancel if undone
    setUndoSnackbar(prev => ({ ...prev, timer }));
  }, [onDelete]);
  
  // Cancel undo timer and restore
  const cancelDeleteAndRestore = useCallback(() => {
    if (undoSnackbar.timer) {
      clearTimeout(undoSnackbar.timer);
    }
    setUndoSnackbar({ open: false, scheduleId: null, scheduleData: null });
    // Refresh to show restored data
    if (onRefresh) onRefresh();
  }, [undoSnackbar.timer, onRefresh]);
  
  // Load custom time slots from database
  const loadCustomTimeSlots = async () => {
    try {
      const result = await getSystemSetting('customTimeSlots');
      if (result.success && result.data) {
        setTimeSlots(result.data);
        // Also cache in localStorage
        localStorage.setItem('adminCustomTimeSlots', JSON.stringify(result.data));
      } else {
        // Fallback to localStorage if API fails
        const saved = localStorage.getItem('adminCustomTimeSlots');
        if (saved) {
          setTimeSlots(JSON.parse(saved));
        }
      }
    } catch (error) {
      console.error('Error loading custom time slots:', error);
      // Fallback to localStorage
      const saved = localStorage.getItem('adminCustomTimeSlots');
      if (saved) {
        setTimeSlots(JSON.parse(saved));
      }
    }
  };
  
  // Auto-set platform if only one option
  useEffect(() => {
    if (availablePlatforms.length === 1 && !platform) {
      setPlatform(availablePlatforms[0]);
    }
  }, [availablePlatforms, platform]);
  
  // Calculate week dates
  useEffect(() => {
    const dates = [];
    const startDate = moment(weekStartDate).startOf('isoWeek'); // Monday
    
    for (let i = 0; i < 7; i++) {
      dates.push(startDate.clone().add(i, 'days'));
    }
    
    setWeekDates(dates);
  }, [weekStartDate]);
  
  // Get schedules for specific day (only current user's schedules)
  const getSchedulesForDay = (dayIndex) => {
    if (!weekDates[dayIndex]) return [];
    
    const targetDate = weekDates[dayIndex].format('YYYY-MM-DD');
    return existingSchedules.filter(schedule => {
      const scheduleDate = moment(schedule.date).format('YYYY-MM-DD');
      const scheduleUserId = schedule.userId?._id || schedule.userId;
      return scheduleDate === targetDate && scheduleUserId === userId;
    });
  };
  
  // Get schedule for specific slot (current user)
  const getScheduleForSlot = (dayIndex, timeSlot) => {
    const daySchedules = getSchedulesForDay(dayIndex);
    const [startTime, endTime] = timeSlot.split('-');
    
    return daySchedules.find(s => 
      s.startTime === startTime && s.endTime === endTime
    );
  };
  
  // Get other users' schedules for specific slot
  const getOthersSchedulesForSlot = (dayIndex, timeSlot) => {
    if (!weekDates[dayIndex]) return [];
    
    const targetDate = weekDates[dayIndex].format('YYYY-MM-DD');
    const [startTime, endTime] = timeSlot.split('-');
    
    // Find schedules from ALL users in this slot
    return existingSchedules.filter(schedule => {
      const scheduleDate = moment(schedule.date).format('YYYY-MM-DD');
      const scheduleUserId = schedule.userId?._id || schedule.userId;
      
      return scheduleDate === targetDate &&
             schedule.startTime === startTime &&
             schedule.endTime === endTime &&
             scheduleUserId !== userId; // Not current user
    });
  };
  
  // Handle slot selection
  const handleSlotToggle = (dayIndex, timeSlot) => {
    // timeSlot format: "07:00-10:00"
    const key = `${dayIndex}-${timeSlot}`;
    console.log('🔑 Creating key:', { dayIndex, timeSlot, key });
    setSelectedSlots(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Slot click with animation
  const handleSlotClickWithAnimation = (dayIndex, slot) => {
    setClickedSlot(`${dayIndex}-${slot}`);
    setTimeout(() => setClickedSlot(null), 300);
    handleSlotToggle(dayIndex, slot);
  };
  
  // Select all slots for a day
  const handleSelectDay = (dayIndex) => {
    const allSelected = timeSlots.every(slot => {
      const existing = getScheduleForSlot(dayIndex, slot);
      return existing || selectedSlots[`${dayIndex}-${slot}`];
    });
    
    const newSelected = { ...selectedSlots };
    timeSlots.forEach(slot => {
      const existing = getScheduleForSlot(dayIndex, slot);
      if (!existing) {
        newSelected[`${dayIndex}-${slot}`] = !allSelected;
      }
    });
    
    setSelectedSlots(newSelected);
  };
  
  // Refresh user data to get latest allowed time slots
  const handleRefreshUserData = async () => {
    setLoading(true);
    try {
      const result = await refreshUserData();
      if (result.success) {
        loadAllowedTimeSlots();
        alert('✅ Đã cập nhật thông tin mới! Bạn có thể thấy khung giờ được phép đăng ký mới nhất.');
      } else {
        alert('❌ ' + (result.error?.message || 'Không thể cập nhật thông tin'));
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
      alert('❌ Có lỗi khi cập nhật thông tin');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle submit
  const handleSubmit = async () => {
    if (!platform) {
      alert('Vui lòng chọn platform');
      return;
    }
    
    const selectedCount = Object.values(selectedSlots).filter(Boolean).length;
    if (selectedCount === 0) {
      alert('Vui lòng chọn ít nhất một khung giờ');
      return;
    }
    
    setLoading(true);
    
    try {
      const schedules = [];
      const conflicts = [];
      
      Object.keys(selectedSlots).forEach(key => {
        if (selectedSlots[key]) {
          // Key format: "dayIndex-HH:mm-HH:mm" 
          // Example: "0-07:00-10:00"
          console.log('🔑 Processing key:', key);
          
          const parts = key.split('-');
          console.log('📦 Parts after split:', parts);
          
          // When split by '-': ["0", "07:00", "10:00"] (3 parts!)
          if (parts.length < 3) {
            console.error('❌ Invalid key format!');
            return;
          }
          
          const dayIndex = parts[0]; // "0"
          // Reconstruct timeSlot from remaining parts (in case time has multiple dashes)
          const timeSlot = parts.slice(1).join('-'); // "07:00-10:00"
          const [startTime, endTime] = timeSlot.split('-'); // ["07:00", "10:00"]
          
          console.log('🔍 Parsed:', { dayIndex, timeSlot, startTime, endTime });
          
          if (!startTime || !endTime) {
            console.error('❌ Could not parse startTime/endTime from:', timeSlot);
            return;
          }
          
          // Check conflict with existing schedules (same platform, same time, different user, status approved/pending)
          const targetDate = weekDates[parseInt(dayIndex)].format('YYYY-MM-DD');
          const hasConflict = existingSchedules.some(schedule => {
            const scheduleDate = moment(schedule.date).format('YYYY-MM-DD');
            const scheduleUserId = schedule.userId?._id || schedule.userId;
            
            return scheduleDate === targetDate &&
                   schedule.startTime === startTime &&
                   schedule.endTime === endTime &&
                   schedule.platform === platform &&
                   scheduleUserId !== userId &&
                   ['pending', 'approved'].includes(schedule.status);
          });
          
          if (hasConflict) {
            conflicts.push(`${DAYS[parseInt(dayIndex)]} ${timeSlot}`);
            return;
          }
          
          // Ensure proper format
          schedules.push({
            userId: userId,
            platform: platform,
            date: targetDate,
            startTime: startTime,
            endTime: endTime,
            notes: notes || '',
            status: 'pending'
          });
        }
      });
      
      // Show conflicts if any
      if (conflicts.length > 0) {
        alert(`⚠️ Các khung giờ sau đã có người đăng ký:\n\n${conflicts.join('\n')}\n\nVui lòng chọn khung giờ khác!`);
        setLoading(false);
        return;
      }
      
      if (schedules.length === 0) {
        alert('Không có khung giờ nào để đăng ký!');
        setLoading(false);
        return;
      }
      
      console.log('📝 Submitting schedules:', schedules);
      
      await onSubmit(schedules);
      
      // Reset
      setSelectedSlots({});
      setNotes('');
      setActiveTab(1); // Switch to registered tab
    } catch (error) {
      console.error('❌ Error submitting schedules:', error);
      alert('Lỗi khi đăng ký lịch: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      case 'completed': return 'info';
      default: return 'default';
    }
  };
  
  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved': return 'Đã duyệt';
      case 'rejected': return 'Từ chối';
      case 'pending': return 'Chờ duyệt';
      case 'completed': return 'Hoàn thành';
      default: return status;
    }
  };
  
  // Get platform color
  const getPlatformColor = (platform) => {
    return platform === 'shopee' ? '#EE4D2D' : '#FE2C55'; // Shopee cam, TikTok hồng
  };
  
  // Get platform info
  const getPlatformInfo = (platform) => {
    if (platform === 'shopee') {
      return {
        icon: ShopeeIcon,
        name: 'Shopee',
        color: '#EE4D2D',
        bgGradient: 'linear-gradient(135deg, #EE4D2D 0%, #FF6F3D 100%)'
      };
    }
    return {
      icon: TikTokIcon,
      name: 'TikTok',
      color: '#FE2C55',
      bgGradient: 'linear-gradient(135deg, #FE2C55 0%, #00F2EA 100%)'
    };
  };
  
  // Filter schedules by status (only current user's schedules)
  const getFilteredSchedules = () => {
    const userSchedules = existingSchedules.filter(s => {
      const scheduleUserId = s.userId?._id || s.userId;
      return scheduleUserId === userId;
    });
    
    if (filterStatus === 'all') return userSchedules;
    return userSchedules.filter(s => s.status === filterStatus);
  };
  
  // Count schedules (only current user's)
  const mySchedules = existingSchedules.filter(s => {
    const scheduleUserId = s.userId?._id || s.userId;
    return scheduleUserId === userId;
  });
  
  const stats = {
    total: mySchedules.length,
    pending: mySchedules.filter(s => s.status === 'pending').length,
    approved: mySchedules.filter(s => s.status === 'approved').length,
    rejected: mySchedules.filter(s => s.status === 'rejected').length
  };

  // Calculate week progress
  const totalPossibleSlots = 7 * timeSlots.length;
  const registeredSlots = mySchedules.length;
  const weekProgress = totalPossibleSlots > 0 ? (registeredSlots / totalPossibleSlots) * 100 : 0;
  
  return (
    <Box 
      ref={containerRef}
      onTouchStart={(e) => { handleTouchStart(e); handleSwipeStart(e); }}
      onTouchMove={(e) => { handleTouchMove(e); handleSwipeMove(e); }}
      onTouchEnd={() => { handleTouchEnd(); handleSwipeEnd(); }}
      sx={{ 
        pb: 10, 
        position: 'relative',
        animation: swipeDirection === 'left' ? `${slideInRight} 0.3s ease` : 
                   swipeDirection === 'right' ? `${slideInLeft} 0.3s ease` : 'none'
      }}
    >
      {/* Pull to Refresh Indicator */}
      <Slide direction="down" in={pullDistance > 30}>
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          display: 'flex',
          justifyContent: 'center',
          pt: 2,
          pb: 1,
          bgcolor: isPulling ? 'primary.main' : 'grey.200',
          color: isPulling ? 'white' : 'text.secondary',
          transition: 'all 0.3s ease'
        }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Refresh sx={{ 
              animation: isPulling ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }} />
            <Typography variant="body2" fontWeight="bold">
              {isPulling ? 'Thả để làm mới...' : 'Kéo xuống để làm mới'}
            </Typography>
          </Stack>
        </Box>
      </Slide>
      
      {/* Swipe Indicator */}
      {swipeDirection && (
        <Box sx={{
          position: 'fixed',
          top: '50%',
          left: swipeDirection === 'right' ? 16 : 'auto',
          right: swipeDirection === 'left' ? 16 : 'auto',
          transform: 'translateY(-50%)',
          zIndex: 1200,
          bgcolor: 'rgba(0,0,0,0.7)',
          color: 'white',
          borderRadius: '50%',
          p: 1.5,
          animation: `${pulseAnimation} 0.5s ease`
        }}>
          {swipeDirection === 'left' ? <ChevronRight fontSize="large" /> : <ChevronLeft fontSize="large" />}
        </Box>
      )}
      
      {/* Stats Bar - Compact */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2,
          mb: 2, 
          borderRadius: 2,
          bgcolor: 'white'
        }}
      >
        {/* Week Progress + Quick Actions */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          {/* Progress Section */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="body2" fontWeight="bold" color="text.secondary">
                📊 Tiến độ tuần
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="primary.main">
                {registeredSlots}/{totalPossibleSlots} slot ({Math.round(weekProgress)}%)
              </Typography>
            </Stack>
            <LinearProgress 
              variant="determinate" 
              value={weekProgress} 
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  background: weekProgress >= 80 
                    ? 'linear-gradient(90deg, #4caf50, #81c784)' 
                    : weekProgress >= 50 
                      ? 'linear-gradient(90deg, #ff9800, #ffb74d)' 
                      : 'linear-gradient(90deg, #667eea, #764ba2)'
                }
              }}
            />
          </Box>
          
          {/* Stats Chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip 
              icon={<CalendarMonth fontSize="small" />}
              label={`${stats.total} lịch`} 
              size="small"
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
            <Chip 
              label={`${stats.pending} chờ`} 
              size="small"
              sx={{ 
                bgcolor: '#fff3e0', 
                color: '#e65100',
                fontWeight: 'bold'
              }}
            />
            <Chip 
              label={`${stats.approved} OK`} 
              size="small"
              sx={{ 
                bgcolor: '#e8f5e9', 
                color: '#2e7d32',
                fontWeight: 'bold'
              }}
            />
          </Stack>
        </Stack>
        
        {/* Quick Actions Row */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          {/* Platform Toggle */}
          {availablePlatforms.length > 1 && (
            <Stack direction="row" spacing={0.5}>
              {availablePlatforms.map(p => {
                const info = getPlatformInfo(p);
                const IconComponent = info.icon;
                return (
                  <Chip
                    key={p}
                    icon={<IconComponent size={16} color={platform === p ? 'white' : info.color} />}
                    label={info.name}
                    size="small"
                    onClick={() => setPlatform(p)}
                    sx={{
                      fontWeight: 'bold',
                      background: platform === p ? info.bgGradient : 'transparent',
                      color: platform === p ? 'white' : info.color,
                      border: `2px solid ${info.color}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        background: platform === p ? info.bgGradient : `${info.color}20`
                      }
                    }}
                  />
                );
              })}
            </Stack>
          )}
          
          {availablePlatforms.length === 1 && platform && (
            <Box>
              {(() => {
                const info = getPlatformInfo(platform);
                const IconComponent = info.icon;
                return (
                  <Chip
                    icon={<IconComponent size={16} color="white" />}
                    label={info.name}
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      background: info.bgGradient,
                      color: 'white'
                    }}
                  />
                );
              })()}
            </Box>
          )}
          
          {/* Calendar Button */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<CalendarMonth />}
            onClick={() => setShowCalendar(true)}
            sx={{ fontWeight: 'bold' }}
          >
            Lịch tháng
          </Button>
        </Stack>
      </Paper>
      
      {/* Calendar Mini View Dialog */}
      <Dialog
        open={showCalendar}
        onClose={() => setShowCalendar(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          pb: 2
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <IconButton 
              size="small" 
              onClick={() => setCalendarMonth(prev => prev.clone().subtract(1, 'month'))}
              sx={{ color: 'white' }}
            >
              <ChevronLeft />
            </IconButton>
            <Typography variant="h6" fontWeight="bold">
              {calendarMonth.format('MMMM YYYY')}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setCalendarMonth(prev => prev.clone().add(1, 'month'))}
              sx={{ color: 'white' }}
            >
              <ChevronRight />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {/* Calendar Grid */}
          <Box sx={{ mb: 2 }}>
            {/* Day headers */}
            <Stack direction="row" sx={{ mb: 1 }}>
              {['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'].map(day => (
                <Box key={day} sx={{ flex: 1, textAlign: 'center' }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {day}
                  </Typography>
                </Box>
              ))}
            </Stack>
            
            {/* Calendar days */}
            {(() => {
              const startOfMonth = calendarMonth.clone().startOf('month');
              const endOfMonth = calendarMonth.clone().endOf('month');
              const startDay = startOfMonth.clone().startOf('isoWeek');
              const endDay = endOfMonth.clone().endOf('isoWeek');
              
              const weeks = [];
              let current = startDay.clone();
              
              while (current.isBefore(endDay)) {
                const week = [];
                for (let i = 0; i < 7; i++) {
                  week.push(current.clone());
                  current.add(1, 'day');
                }
                weeks.push(week);
              }
              
              return weeks.map((week, weekIdx) => (
                <Stack key={weekIdx} direction="row" sx={{ mb: 0.5 }}>
                  {week.map((day, dayIdx) => {
                    const isCurrentMonth = day.month() === calendarMonth.month();
                    const isToday = day.isSame(moment(), 'day');
                    const isInSelectedWeek = weekDates.some(d => d.isSame(day, 'day'));
                    const hasSchedule = existingSchedules.some(s => {
                      const scheduleDate = moment(s.date).format('YYYY-MM-DD');
                      const scheduleUserId = s.userId?._id || s.userId;
                      return scheduleDate === day.format('YYYY-MM-DD') && scheduleUserId === userId;
                    });
                    
                    return (
                      <Box 
                        key={dayIdx}
                        onClick={() => {
                          if (onWeekChange) {
                            // Calculate which week to go to
                            const targetWeekStart = day.clone().startOf('isoWeek');
                            const currentWeekStart = moment(weekStartDate).startOf('isoWeek');
                            const weeksDiff = targetWeekStart.diff(currentWeekStart, 'weeks');
                            
                            if (weeksDiff > 0) {
                              for (let i = 0; i < weeksDiff; i++) onWeekChange('next');
                            } else if (weeksDiff < 0) {
                              for (let i = 0; i < Math.abs(weeksDiff); i++) onWeekChange('prev');
                            }
                          }
                          setShowCalendar(false);
                        }}
                        sx={{ 
                          flex: 1, 
                          textAlign: 'center',
                          py: 0.8,
                          cursor: 'pointer',
                          borderRadius: 1,
                          position: 'relative',
                          bgcolor: isInSelectedWeek 
                            ? 'primary.100' 
                            : isToday 
                              ? 'success.50' 
                              : 'transparent',
                          border: isToday ? '2px solid' : 'none',
                          borderColor: 'success.main',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'grey.100',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          fontWeight={isToday || isInSelectedWeek ? 'bold' : 'normal'}
                          sx={{ 
                            color: !isCurrentMonth 
                              ? 'grey.400' 
                              : isToday 
                                ? 'success.main' 
                                : isInSelectedWeek 
                                  ? 'primary.main' 
                                  : 'text.primary'
                          }}
                        >
                          {day.date()}
                        </Typography>
                        {/* Schedule indicator dot */}
                        {hasSchedule && (
                          <Box sx={{
                            position: 'absolute',
                            bottom: 2,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main'
                          }} />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              ));
            })()}
          </Box>
          
          {/* Legend */}
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={2} justifyContent="center">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main' }} />
              <Typography variant="caption">Hôm nay</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'primary.100' }} />
              <Typography variant="caption">Tuần đang chọn</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
              <Typography variant="caption">Có lịch</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            fullWidth 
            variant="outlined"
            onClick={() => {
              setCalendarMonth(moment());
              // Jump to current week
              if (onWeekChange) {
                const currentWeekStart = moment().startOf('isoWeek');
                const selectedWeekStart = moment(weekStartDate).startOf('isoWeek');
                const weeksDiff = currentWeekStart.diff(selectedWeekStart, 'weeks');
                
                if (weeksDiff > 0) {
                  for (let i = 0; i < weeksDiff; i++) onWeekChange('next');
                } else if (weeksDiff < 0) {
                  for (let i = 0; i < Math.abs(weeksDiff); i++) onWeekChange('prev');
                }
              }
              setShowCalendar(false);
            }}
            startIcon={<Today />}
          >
            Về tuần hiện tại
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          variant="fullWidth"
          indicatorColor="primary"
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Add fontSize="small" />
                Đăng ký mới
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Badge badgeContent={stats.total} color="primary">
                  <Schedule fontSize="small" />
                </Badge>
                Đã đăng ký
              </Box>
            }
          />
        </Tabs>
      </Paper>
      
      {/* Tab 0: Register new schedules */}
      {activeTab === 0 && (
        <Box>
          {/* Info Alert with Refresh Button */}
          {allowedTimeSlots.length > 0 && (
            <Alert 
              severity="info" 
              sx={{ mb: 2, borderRadius: 2 }}
              action={
                <Button 
                  size="small" 
                  onClick={handleRefreshUserData}
                  disabled={loading}
                  startIcon={<Refresh />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Cập nhật
                </Button>
              }
            >
              <Typography variant="caption" fontWeight="bold">
                🔒 Admin đã giới hạn khung giờ đăng ký của bạn ({allowedTimeSlots.length} khung giờ)
              </Typography>
            </Alert>
          )}
          
          {/* Platform selection */}
          {availablePlatforms.length > 1 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                1️⃣ Chọn nền tảng
              </Typography>
              <Stack direction="row" spacing={1.5}>
                {availablePlatforms.includes('shopee') && (
                  <Button
                    variant={platform === 'shopee' ? 'contained' : 'outlined'}
                    onClick={() => setPlatform('shopee')}
                    size="large"
                    fullWidth
                    startIcon={<ShopeeIcon size={24} color={platform === 'shopee' ? 'white' : '#EE4D2D'} />}
                    sx={{
                      py: 2,
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      background: platform === 'shopee' 
                        ? 'linear-gradient(135deg, #EE4D2D 0%, #FF6F3D 100%)'
                        : 'white',
                      color: platform === 'shopee' ? 'white' : '#EE4D2D',
                      borderColor: '#EE4D2D',
                      borderWidth: 2,
                      '&:hover': {
                        background: platform === 'shopee'
                          ? 'linear-gradient(135deg, #D63C1C 0%, #EE5F2D 100%)'
                          : 'rgba(238, 77, 45, 0.1)',
                        borderWidth: 2
                      }
                    }}
                  >
                    Shopee
                  </Button>
                )}
                {availablePlatforms.includes('tiktok') && (
                  <Button
                    variant={platform === 'tiktok' ? 'contained' : 'outlined'}
                    onClick={() => setPlatform('tiktok')}
                    size="large"
                    fullWidth
                    startIcon={<TikTokIcon size={24} color={platform === 'tiktok' ? 'white' : '#FE2C55'} />}
                    sx={{
                      py: 2,
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      background: platform === 'tiktok'
                        ? 'linear-gradient(135deg, #FE2C55 0%, #00F2EA 100%)'
                        : 'white',
                      color: platform === 'tiktok' ? 'white' : '#FE2C55',
                      borderColor: '#FE2C55',
                      borderWidth: 2,
                      '&:hover': {
                        background: platform === 'tiktok'
                          ? 'linear-gradient(135deg, #E51B44 0%, #00D9D0 100%)'
                          : 'rgba(254, 44, 85, 0.1)',
                        borderWidth: 2
                      }
                    }}
                  >
                    TikTok
                  </Button>
                )}
              </Stack>
            </Paper>
          )}
          
          {/* Day selection */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              2️⃣ Chọn ca live ({Object.values(selectedSlots).filter(Boolean).length} đã chọn)
            </Typography>
            
            {/* Day List */}
            <Stack spacing={1.5}>
              {weekDates.map((date, dayIndex) => {
                const daySchedules = getSchedulesForDay(dayIndex);
                const selectedCount = timeSlots.filter(slot => 
                  selectedSlots[`${dayIndex}-${slot}`]
                ).length;
                const isExpanded = expandedDay === dayIndex;
                const isToday = date.isSame(moment(), 'day');
                
                return (
                  <Grow in={true} style={{ transformOrigin: '0 0 0', transitionDelay: `${dayIndex * 50}ms` }} key={dayIndex}>
                  <Card 
                    id={`day-${dayIndex}`}
                    variant="outlined"
                    sx={{ 
                      borderLeft: 4,
                      borderColor: isToday ? 'primary.main' : 'grey.300',
                      bgcolor: isToday ? 'primary.50' : 'white',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={() => setExpandedDay(isExpanded ? null : dayIndex)}
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {DAYS[dayIndex]}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {date.format('DD/MM/YYYY')}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {selectedCount > 0 && (
                            <Chip 
                              label={`${selectedCount} ca`} 
                              size="small" 
                              color="primary"
                            />
                          )}
                          {daySchedules.length > 0 && (
                            <Chip 
                              label={`${daySchedules.length} đã có`} 
                              size="small" 
                              color="default"
                            />
                          )}
                          <IconButton size="small">
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Collapse in={isExpanded}>
                        <Divider sx={{ my: 1 }} />
                        
                        {getFilteredTimeSlots(dayIndex, platform).length > 0 && (
                          <Button 
                            size="small" 
                            onClick={() => handleSelectDay(dayIndex)}
                            fullWidth
                            variant="outlined"
                            sx={{ mb: 1 }}
                          >
                            Chọn tất cả ngày này
                          </Button>
                        )}
                        
                        <Stack spacing={1}>
                          {getFilteredTimeSlots(dayIndex, platform).length === 0 && allowedTimeSlots.length > 0 ? (
                            <Alert severity="warning" sx={{ borderRadius: 2 }}>
                              <Typography variant="caption">
                                ⚠️ Không có khung giờ nào được phép đăng ký cho {DAYS[dayIndex]} {platform ? `trên ${getPlatformInfo(platform).name}` : ''}
                              </Typography>
                            </Alert>
                          ) : (
                            getFilteredTimeSlots(dayIndex, platform).map(slot => {
                            const key = `${dayIndex}-${slot}`;
                            const existing = getScheduleForSlot(dayIndex, slot);
                            const othersSchedules = getOthersSchedulesForSlot(dayIndex, slot);
                            const isSelected = selectedSlots[key];
                            
                            // Show current user's existing schedule (disabled)
                            if (existing) {
                              return (
                                <Box 
                                  key={slot}
                                  sx={{ 
                                    p: 1, 
                                    bgcolor: 'grey.100',
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: 'grey.300'
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <AccessTime fontSize="small" color="disabled" />
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                      {slot}
                                    </Typography>
                                    <Chip
                                      label={getStatusLabel(existing.status)}
                                      size="small"
                                      color={getStatusColor(existing.status)}
                                    />
                                  </Stack>
                                </Box>
                              );
                            }
                            
                            // Check if slot is already taken by someone else (same platform)
                            const slotTaken = othersSchedules.some(s => 
                              s.platform === platform && 
                              ['pending', 'approved'].includes(s.status)
                            );
                            
                            // Show slot already taken (disabled)
                            if (slotTaken && platform) {
                              const takenBy = othersSchedules.find(s => 
                                s.platform === platform && 
                                ['pending', 'approved'].includes(s.status)
                              );
                              const takenUserName = takenBy?.userId?.fullName || 
                                                   takenBy?.userId?.username || 
                                                   'N/A';
                              
                              return (
                                <Box 
                                  key={slot}
                                  sx={{ 
                                    p: 2, 
                                    bgcolor: 'error.50',
                                    borderRadius: 2,
                                    border: 2,
                                    borderColor: 'error.main',
                                    borderStyle: 'dashed'
                                  }}
                                >
                                  <Stack spacing={1}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Cancel fontSize="small" color="error" />
                                      <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>
                                        {slot}
                                      </Typography>
                                      <Chip
                                        label="Đã đầy"
                                        size="small"
                                        color="error"
                                        sx={{ fontWeight: 'bold' }}
                                      />
                                    </Stack>
                                    <Alert severity="warning" sx={{ py: 0 }}>
                                      <Typography variant="caption" fontWeight="bold">
                                        🔒 {takenUserName} đã đăng ký {getPlatformInfo(platform).name}
                                      </Typography>
                                    </Alert>
                                  </Stack>
                                </Box>
                              );
                            }
                            
                            // Show available slot with others' info - Enhanced with ripple animation
                            const slotKey = `${dayIndex}-${slot}`;
                            const isClicked = clickedSlot === slotKey;
                            
                            return (
                              <Box key={slot}>
                                <Box
                                  onClick={() => handleSlotClickWithAnimation(dayIndex, slot)}
                                  sx={{
                                    p: 2,
                                    bgcolor: isSelected ? 'primary.main' : 'white',
                                    color: isSelected ? 'white' : 'text.primary',
                                    border: 2,
                                    borderColor: isSelected ? 'primary.main' : 'grey.300',
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: isSelected ? 4 : 1,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    transform: isClicked ? 'scale(0.97)' : 'scale(1)',
                                    '&:hover': {
                                      boxShadow: 4,
                                      borderColor: 'primary.main',
                                      bgcolor: isSelected ? 'primary.dark' : 'grey.50',
                                      transform: 'translateY(-2px)'
                                    },
                                    '&:active': {
                                      transform: 'scale(0.97)'
                                    },
                                    // Ripple effect
                                    '&::after': isClicked ? {
                                      content: '""',
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      width: 10,
                                      height: 10,
                                      bgcolor: isSelected ? 'rgba(255,255,255,0.5)' : 'rgba(102,126,234,0.3)',
                                      borderRadius: '50%',
                                      animation: `${rippleAnimation} 0.4s ease-out`
                                    } : {}
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Zoom in={true} style={{ transitionDelay: '50ms' }}>
                                      {isSelected ? (
                                        <CheckCircle sx={{ 
                                          color: 'white', 
                                          fontSize: 28,
                                          animation: isClicked ? `${pulseAnimation} 0.3s ease` : 'none'
                                        }} />
                                      ) : (
                                        <Schedule sx={{ fontSize: 28 }} color="action" />
                                      )}
                                    </Zoom>
                                    <Typography 
                                      variant="h6" 
                                      fontWeight="bold"
                                      sx={{ flex: 1 }}
                                    >
                                      {slot}
                                    </Typography>
                                    {othersSchedules.length > 0 && (
                                      <Chip
                                        label={`${othersSchedules.length} 👤`}
                                        size="small"
                                        sx={{ 
                                          fontSize: '0.75rem',
                                          fontWeight: 'bold',
                                          bgcolor: isSelected ? 'rgba(255,255,255,0.3)' : 'grey.200'
                                        }}
                                      />
                                    )}
                                  </Stack>
                                  
                                  {/* Show who registered (other platforms only) */}
                                  {(() => {
                                    const otherPlatformUsers = othersSchedules.filter(s => s.platform !== platform);
                                    if (otherPlatformUsers.length === 0 || !platform) return null;
                                    
                                    return (
                                      <Box sx={{ 
                                        mt: 1.5, 
                                        pt: 1.5, 
                                        borderTop: 1, 
                                        borderColor: isSelected ? 'rgba(255,255,255,0.3)' : 'divider'
                                      }}>
                                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                          <Typography 
                                            variant="caption" 
                                            fontWeight="bold"
                                            sx={{ 
                                              color: isSelected ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                                              mb: 0.5
                                            }}
                                          >
                                            👥 Platform khác:
                                          </Typography>
                                          {otherPlatformUsers.map((schedule, idx) => {
                                            const userName = schedule.userId?.fullName || 
                                                           schedule.userId?.username || 
                                                           'N/A';
                                            const platformInfo = getPlatformInfo(schedule.platform);
                                            const IconComponent = platformInfo.icon;
                                            
                                            return (
                                              <Chip
                                                key={idx}
                                                icon={<IconComponent size={16} color="white" />}
                                                label={userName}
                                                size="small"
                                                sx={{ 
                                                  fontSize: '0.7rem',
                                                  height: 24,
                                                  fontWeight: 'bold',
                                                  background: isSelected 
                                                    ? 'rgba(255,255,255,0.25)' 
                                                    : platformInfo.bgGradient,
                                                  color: 'white',
                                                  border: 1,
                                                  borderColor: isSelected ? 'rgba(255,255,255,0.4)' : platformInfo.color
                                                }}
                                              />
                                            );
                                          })}
                                        </Stack>
                                      </Box>
                                    );
                                  })()}
                                </Box>
                              </Box>
                            );
                          })
                          )}
                        </Stack>
                      </Collapse>
                    </CardContent>
                  </Card>
                  </Grow>
                );
              })}
            </Stack>
          </Paper>
          
          {/* Notes */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              3️⃣ Ghi chú (không bắt buộc)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="Thêm ghi chú cho lịch live..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Paper>
          
          {/* Submit button */}
          <Paper 
            sx={{ 
              position: 'fixed', 
              bottom: 0, 
              left: 0, 
              right: 0, 
              p: 2,
              zIndex: 1000,
              boxShadow: 3
            }}
          >
            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || !platform || Object.values(selectedSlots).filter(Boolean).length === 0}
              onClick={handleSubmit}
              startIcon={<CheckCircle />}
            >
              {loading ? 'Đang gửi...' : `Đăng ký ${Object.values(selectedSlots).filter(Boolean).length} ca live`}
            </Button>
          </Paper>
        </Box>
      )}
      
      {/* Tab 1: View registered schedules */}
      {activeTab === 1 && (
        <Box>
          {/* Filter & View Toggle */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl fullWidth size="small">
                  <InputLabel>Lọc theo trạng thái</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Lọc theo trạng thái"
                  >
                    <MenuItem value="all">Tất cả ({stats.total})</MenuItem>
                    <MenuItem value="pending">Chờ duyệt ({stats.pending})</MenuItem>
                    <MenuItem value="approved">Đã duyệt ({stats.approved})</MenuItem>
                    <MenuItem value="rejected">Từ chối ({stats.rejected})</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              
              <ToggleButtonGroup
                value={viewType}
                exclusive
                onChange={(e, v) => v && setViewType(v)}
                fullWidth
                size="small"
              >
                <ToggleButton value="timeline">
                  <Timeline sx={{ mr: 0.5 }} fontSize="small" />
                  Lịch tuần
                </ToggleButton>
                <ToggleButton value="list">
                  <FilterList sx={{ mr: 0.5 }} fontSize="small" />
                  Chi tiết
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>
          
          {/* Timeline View */}
          {viewType === 'timeline' && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                📌 Click vào lịch để xem chi tiết
              </Alert>
              
              <Stack spacing={1.5}>
                {weekDates.map((date, dayIndex) => {
                  const daySchedules = getSchedulesForDay(dayIndex).filter(s => 
                    filterStatus === 'all' || s.status === filterStatus
                  );
                  
                  const isToday = date.isSame(moment(), 'day');
                  
                  return (
                    <Paper 
                      key={dayIndex}
                      elevation={isToday ? 4 : 1}
                      sx={{ 
                        p: 2,
                        bgcolor: isToday ? 'primary.50' : 'white',
                        border: 2,
                        borderColor: isToday ? 'primary.main' : 'grey.200',
                        borderRadius: 2
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <CalendarMonth 
                          fontSize="small" 
                          color={isToday ? 'primary' : 'action'}
                        />
                        <Typography 
                          variant="subtitle1" 
                          fontWeight="bold"
                          color={isToday ? 'primary' : 'text.primary'}
                        >
                          {DAYS[dayIndex]} - {date.format('DD/MM')}
                        </Typography>
                        {isToday && (
                          <Chip 
                            label="Hôm nay" 
                            size="small" 
                            color="primary"
                            sx={{ fontWeight: 'bold' }}
                          />
                        )}
                        <Box flex={1} />
                        <Chip 
                          label={`${daySchedules.length} ca`}
                          size="small"
                          color={daySchedules.length > 0 ? 'success' : 'default'}
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Stack>
                      
                      {daySchedules.length > 0 ? (
                        <Stack spacing={1}>
                          {daySchedules.map(schedule => (
                            <Box
                              key={schedule._id}
                              onClick={() => {
                                setViewType('list');
                              }}
                              sx={{
                                p: 1.5,
                                bgcolor: 'white',
                                borderRadius: 1,
                                border: 1,
                                borderLeft: 4,
                                borderColor: getPlatformColor(schedule.platform),
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  boxShadow: 2,
                                  transform: 'translateX(4px)'
                                }
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <AccessTime fontSize="small" />
                                <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>
                                  {schedule.startTime} - {schedule.endTime}
                                </Typography>
                                {(() => {
                                  const platformInfo = getPlatformInfo(schedule.platform);
                                  const IconComponent = platformInfo.icon;
                                  return (
                                    <Chip
                                      icon={<IconComponent size={16} color="white" />}
                                      label={platformInfo.name}
                                      size="small"
                                      sx={{
                                        background: platformInfo.bgGradient,
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                  );
                                })()}
                                <Chip
                                  label={getStatusLabel(schedule.status)}
                                  size="small"
                                  color={getStatusColor(schedule.status)}
                                />
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Box 
                          sx={{ 
                            p: 2, 
                            textAlign: 'center',
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: 1,
                            borderStyle: 'dashed',
                            borderColor: 'grey.300'
                          }}
                        >
                          <Typography variant="body2" color="text.disabled">
                            📭 Chưa có lịch
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          )}
          
          {/* List View */}
          {viewType === 'list' && (
            <Stack spacing={1.5}>
              {weekDates.map((date, dayIndex) => {
                const daySchedules = getSchedulesForDay(dayIndex).filter(s => 
                  filterStatus === 'all' || s.status === filterStatus
                );
                
                if (daySchedules.length === 0) return null;
              
              return (
                <Paper key={dayIndex} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {DAYS[dayIndex]} - {date.format('DD/MM')}
                  </Typography>
                  
                  <Stack spacing={1}>
                    {daySchedules.map(schedule => (
                      <Card 
                        key={schedule._id}
                        elevation={2}
                        sx={{
                          borderLeft: 6,
                          borderColor: getPlatformColor(schedule.platform),
                          borderRadius: 2,
                          overflow: 'hidden',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: 4,
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          {/* Debug log */}
                          {console.log('📅 Rendering schedule:', schedule)}
                          
                          {/* Time */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mb: 1.5,
                              p: 1.5,
                              bgcolor: 'grey.50',
                              borderRadius: 1
                            }}
                          >
                            <AccessTime sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
                              {schedule.startTime || 'N/A'} - {schedule.endTime || 'N/A'}
                            </Typography>
                            {(() => {
                              const platformInfo = getPlatformInfo(schedule.platform);
                              const IconComponent = platformInfo.icon;
                              return (
                                <Chip
                                  icon={<IconComponent size={20} color="white" />}
                                  label={platformInfo.name}
                                  sx={{ 
                                    background: platformInfo.bgGradient,
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    height: 28,
                                    '& .MuiChip-label': { px: 1.5 }
                                  }}
                                />
                              );
                            })()}
                          </Box>
                          
                          {/* Status */}
                          <Box sx={{ mb: 1 }}>
                            <Chip
                              label={getStatusLabel(schedule.status)}
                              size="medium"
                              color={getStatusColor(schedule.status)}
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                          
                          {/* Notes */}
                          {schedule.notes && (
                            <Box sx={{ 
                              mt: 1.5, 
                              p: 1.5, 
                              bgcolor: 'info.50', 
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'info.200'
                            }}>
                              <Typography variant="body2" color="text.secondary">
                                💬 {schedule.notes}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Rejection Reason */}
                          {schedule.rejectionReason && (
                            <Alert severity="error" sx={{ mt: 1.5 }}>
                              <Typography variant="body2" fontWeight="bold">
                                ❌ Lý do từ chối:
                              </Typography>
                              <Typography variant="body2">
                                {schedule.rejectionReason}
                              </Typography>
                            </Alert>
                          )}
                        </CardContent>
                        
                        {/* Actions */}
                        {schedule.status === 'pending' && onDelete && (
                          <CardActions sx={{ bgcolor: 'grey.50', px: 2, py: 1.5 }}>
                            <Button 
                              variant="outlined"
                              size="medium" 
                              color="error"
                              startIcon={<Delete />}
                              fullWidth
                              onClick={() => handleDeleteWithUndo(schedule._id, schedule)}
                              sx={{ fontWeight: 'bold' }}
                            >
                              Xóa lịch
                            </Button>
                          </CardActions>
                        )}
                      </Card>
                    ))}
                  </Stack>
                </Paper>
              );
            })}
            
              {getFilteredSchedules().length === 0 && (
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    bgcolor: 'grey.50',
                    borderRadius: 3,
                    border: '2px dashed',
                    borderColor: 'grey.300'
                  }}
                >
                  {/* Empty State Illustration */}
                  <Box sx={{ 
                    fontSize: 80, 
                    mb: 2,
                    animation: `${pulseAnimation} 2s ease-in-out infinite`
                  }}>
                    📅
                  </Box>
                  <Typography variant="h6" color="text.secondary" fontWeight="bold" gutterBottom>
                    Chưa có lịch nào
                  </Typography>
                  <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                    Bạn chưa đăng ký lịch livestream nào trong tuần này
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setActiveTab(0)}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontWeight: 'bold'
                    }}
                  >
                    Đăng ký ngay
                  </Button>
                </Paper>
              )}
            </Stack>
          )}
          
          {/* FAB Group - Refresh & Today */}
          <Stack 
            spacing={1.5} 
            sx={{ 
              position: 'fixed', 
              bottom: 80, 
              right: 16,
              zIndex: 1000
            }}
          >
            <Zoom in={true} style={{ transitionDelay: '100ms' }}>
              <Fab
                size="medium"
                color="secondary"
                onClick={() => {
                  // Scroll to today
                  const todayIndex = weekDates.findIndex(d => d.isSame(moment(), 'day'));
                  if (todayIndex >= 0) {
                    setExpandedDay(todayIndex);
                    // Scroll into view
                    setTimeout(() => {
                      const element = document.getElementById(`day-${todayIndex}`);
                      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                  }
                }}
                sx={{
                  bgcolor: '#4caf50',
                  '&:hover': { bgcolor: '#388e3c' }
                }}
              >
                <Today />
              </Fab>
            </Zoom>
            <Zoom in={true}>
              <Fab
                color="primary"
                onClick={onRefresh}
              >
                <Refresh />
              </Fab>
            </Zoom>
          </Stack>
        </Box>
      )}
      
      {/* Undo Delete Snackbar */}
      <Snackbar
        open={undoSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setUndoSnackbar({ open: false, scheduleId: null, scheduleData: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 140, sm: 24 } }}
      >
        <Alert
          severity="warning"
          variant="filled"
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={cancelDeleteAndRestore}
              startIcon={<Undo />}
              sx={{ fontWeight: 'bold' }}
            >
              HOÀN TÁC
            </Button>
          }
          sx={{ 
            width: '100%',
            alignItems: 'center',
            '& .MuiAlert-action': { pt: 0 }
          }}
        >
          Đã xóa lịch. Hoàn tác trong 5s...
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MobileScheduleSelector;

